import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { LifeBuoy, Check, X, UserCog, Clock } from 'lucide-react';

/**
 * Pop-up globale lato POS: controlla ogni 3s se il Gestore assegnato ha chiesto
 * di accedere a questo locale e mostra Accetta/Rifiuta ovunque ci si trovi nell'app.
 */
export const IncomingSupportModal = () => {
  const { t } = useTranslation();
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(false);
  const announcedRef = useRef(new Set());

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get('/remote/support/incoming');
      if (!data?.pending || !data.request) { setReq(null); return; }
      const r = data.request;
      setReq((cur) => (cur && cur.id === r.id ? { ...cur, expires_in: r.expires_in } : r));
      if (!announcedRef.current.has(r.id)) {
        announcedRef.current.add(r.id);
        toast.info(t('remote.incomingToast', { defaultValue: 'Il gestore chiede di accedere al POS' }));
      }
    } catch (e) { /* silenzioso: token assente o rete */ }
  }, [t]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [poll]);

  const respond = async (action) => {
    if (!req || busy) return;
    setBusy(true);
    try {
      await api.post(`/remote/support/${req.id}/respond`, { action });
      setReq(null);
      if (action === 'accepted') toast.success(t('remote.youAccepted', { defaultValue: 'Accesso autorizzato al gestore' }));
      else toast(t('remote.youDeclined', { defaultValue: 'Accesso rifiutato' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('remote.error', { defaultValue: 'Errore' }));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) setReq(null); }}>
      <DialogContent data-testid="pos-incoming-support-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" /> {t('remote.incomingTitle', { defaultValue: 'Richiesta di accesso remoto' })}
          </DialogTitle>
          <DialogDescription>
            {t('remote.incomingDesc', { defaultValue: 'Il tuo gestore chiede di vedere e controllare questo POS per assistenza.' })}
          </DialogDescription>
        </DialogHeader>
        {req && (
          <div className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid="pos-incoming-support-info">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0"><UserCog className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{req.requested_by_name || t('remote.manager', { defaultValue: 'Gestore' })}</div>
              <div className="text-sm text-muted-foreground truncate">{req.message || t('remote.noMessage', { defaultValue: 'Nessun messaggio' })}</div>
            </div>
            {req.expires_in > 0 && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0" data-testid="pos-incoming-support-expires">
                <Clock className="h-3.5 w-3.5" /> {req.expires_in}s
              </span>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="border-rose-500/40 text-rose-600" disabled={busy} onClick={() => respond('declined')} data-testid="pos-incoming-support-decline">
            <X className="h-4 w-4 mr-1" /> {t('remote.decline', { defaultValue: 'Rifiuta' })}
          </Button>
          <Button disabled={busy} onClick={() => respond('accepted')} data-testid="pos-incoming-support-accept">
            <Check className="h-4 w-4 mr-1" /> {t('remote.accept', { defaultValue: 'Accetta' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
